import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Subscription, mapSubscriptionFromDb } from '@/services/types/subscriptionTypes';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { CircleDollarSign, Loader2, Pause, Play, Ban, CreditCard, Pencil } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { updateSubscriptionStatus } from '@/pages/admin/rentals/services/subscriptionUpdate';
import { validateInterval, getIntervalName, hasActiveSubscription } from '@/pages/admin/rentals/services/subscriptionCommon';
import { AddSubscriptionForm } from './AddSubscriptionForm';
import { EditSubscriptionForm } from './EditSubscriptionForm';

interface RentalSubscriptionsTabProps {
  rentalId: string;
  onUpdate?: () => void;
}

export const RentalSubscriptionsTab: React.FC<RentalSubscriptionsTabProps> = ({ 
  rentalId,
  onUpdate 
}) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);

  // Функция загрузки подписок
  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Получаем подписки для данной аренды
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('rental_id', rentalId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Ошибка при загрузке подписок:', error);
        setError('Ошибка при загрузке данных о подписках');
        return;
      }
      
      if (data) {
        // Преобразуем данные к правильному типу используя утилиту мапинга
        const typedSubscriptions: Subscription[] = data.map(sub => mapSubscriptionFromDb(sub));
        
        setSubscriptions(typedSubscriptions);
        console.log(`Загружено ${data.length} подписок для аренды ${rentalId}`);
      }
    } catch (err) {
      console.error('Ошибка при загрузке подписок:', err);
      setError('Произошла ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (rentalId) {
      fetchSubscriptions();
    }
  }, [rentalId]);

  // Обработчики действий с подпиской
  const handleSubscriptionAction = async (subscriptionId: string, newStatus: 'active' | 'cancelled' | 'paused') => {
    try {
      setActionLoading(subscriptionId);
      
      const { success, error } = await updateSubscriptionStatus(subscriptionId, newStatus);
      
      if (success) {
        toast.success(`Статус подписки успешно изменен на ${newStatus}`);
        
        // Обновл��ем локальный список подписок
        setSubscriptions(subscriptions.map(sub => 
          sub.id === subscriptionId ? { ...sub, status: newStatus } : sub
        ));
        
        // Уведомляем родительский компонент об обновлении
        if (onUpdate) {
          onUpdate();
        }
      } else {
        toast.error(`Ошибка при изменении статуса подписки: ${error}`);
      }
    } catch (err) {
      console.error('Ошибка при обновлении статуса подписки:', err);
      toast.error('Произошла ошибка при обновлении статуса');
    } finally {
      setActionLoading(null);
    }
  };
  
  // Обработчик открытия формы редактирования подписки
  const handleEditSubscription = (subscription: Subscription) => {
    setEditingSubscription(subscription);
  };
  
  // Обработчик закрытия формы редактирования
  const handleCloseEditForm = () => {
    setEditingSubscription(null);
  };

  // Функция форматирования даты
  const formatDate = (dateString: string | Date) => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'dd.MM.yyyy', { locale: ru });
    } catch (error) {
      return 'Некорректная дата';
    }
  };
  
  return (
    <Card>
      <CardContent className="p-4">
        {/* Форма создания новой подписки с передачей существующих подписок */}
        <AddSubscriptionForm 
          rentalId={rentalId} 
          onSubscriptionAdded={fetchSubscriptions} 
          existingSubscriptions={subscriptions}
        />
        
        {/* Состояние загрузки */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Загрузка подписок...</span>
          </div>
        ) : error ? (
          <div className="text-center text-destructive py-6">{error}</div>
        ) : subscriptions.length === 0 ? (
          <div className="text-center text-muted-foreground py-6">
            Для этой аренды не найдено подписок
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Дата создания</TableHead>
                <TableHead>Периодичность</TableHead>
                <TableHead>Сумма</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>След. платеж</TableHead>
                <TableHead>Карта</TableHead>
                <TableHead>Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.map(subscription => (
                <TableRow key={subscription.id}>
                  <TableCell>{formatDate(subscription.startDate || subscription.start_date || '')}</TableCell>
                  <TableCell>{getIntervalName(subscription.interval || 'month')}</TableCell>
                  <TableCell>{subscription.amount} ₽</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        subscription.status === 'active' ? 'success' :
                        subscription.status === 'paused' ? 'warning' : 'destructive'
                      }
                      className="flex items-center gap-1"
                    >
                      {subscription.status === 'active' && <CircleDollarSign className="h-3 w-3" />}
                      {subscription.status === 'paused' && <Pause className="h-3 w-3" />}
                      {subscription.status === 'cancelled' && <Ban className="h-3 w-3" />}
                      {subscription.status === 'active' ? 'Активна' : 
                       subscription.status === 'paused' ? 'Приостановлена' : 'Отменена'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {subscription.nextPaymentDate || subscription.next_payment_date ? 
                      formatDate(subscription.nextPaymentDate || subscription.next_payment_date || '') : 
                      subscription.status === 'cancelled' ? '—' : 'Не указана'
                    }
                  </TableCell>
                  <TableCell>
                    {(subscription.cardLast4 || subscription.card_last4) ? (
                      <div className="flex items-center">
                        <CreditCard className="h-4 w-4 mr-1" />
                        <span>
                          {subscription.cardType || subscription.card_type} *{subscription.cardLast4 || subscription.card_last4}
                        </span>
                      </div>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      {/* Кнопка редактирования */}
                      {subscription.status !== 'cancelled' && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!!actionLoading}
                          onClick={() => handleEditSubscription(subscription)}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Изменить
                        </Button>
                      )}
                      
                      {subscription.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!!actionLoading}
                          onClick={() => handleSubscriptionAction(subscription.id, 'paused')}
                        >
                          {actionLoading === subscription.id ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Pause className="h-4 w-4 mr-1" />
                          )}
                          Пауза
                        </Button>
                      )}
                      
                      {subscription.status === 'paused' && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!!actionLoading}
                          onClick={() => handleSubscriptionAction(subscription.id, 'active')}
                        >
                          {actionLoading === subscription.id ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Play className="h-4 w-4 mr-1" />
                          )}
                          Возобновить
                        </Button>
                      )}
                      
                      {subscription.status !== 'cancelled' && (
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={!!actionLoading}
                          onClick={() => handleSubscriptionAction(subscription.id, 'cancelled')}
                        >
                          {actionLoading === subscription.id ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Ban className="h-4 w-4 mr-1" />
                          )}
                          Отменить
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      
      {/* Диалог редактирования подписки */}
      {editingSubscription && (
        <EditSubscriptionForm
          subscription={editingSubscription}
          open={!!editingSubscription}
          onOpenChange={handleCloseEditForm}
          onSubscriptionUpdated={fetchSubscriptions}
        />
      )}
    </Card>
  );
};
